import { PostFormState } from "../../../shared/types/post";
import { hideModal, showConfirmModal } from "../../../actions/modal";
import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import {
  closeBulkCreatePostModal,
  closePostFormInBulkCreate,
  setIsFormDirty,
} from "./bulk-create-post.actions";
import { ChildCallbacks } from "../../../components/Posts/PostForm/types";

export const useChildCallbacks = ({
  onChildCallbacksRefChange,
}: {
  onChildCallbacksRefChange: (callbacks: ChildCallbacks | undefined) => void;
}) => {
  const childrenCallbacksRef = useRef<ChildCallbacks>({});
  useEffect(() => {
    onChildCallbacksRefChange(childrenCallbacksRef.current);
    return () => {
      // eslint-disable-next-line unicorn/no-useless-undefined
      onChildCallbacksRefChange(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return childrenCallbacksRef.current;
};

export const useHandleFormDirty = ({
  dispatch,
}: {
  dispatch: Dispatch<any>;
}) => {
  const [isFormSettled, setIsFormSettled] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setIsFormSettled(true);
    }, 2000); /** it's possible some initial logic when opening the modal causing some value changes,
    delay 2 seconds to ensure all initial state is applied */
  }, []);
  return {
    handleOnFormValueChanged: useCallback(
      (id: string, isChanged: boolean) => {
        if (isFormSettled) {
          dispatch(setIsFormDirty({ id, isDirty: isChanged }));
        }
      },
      [isFormSettled, dispatch]
    ),
  };
};
export function closeFormWithConfirm({
  form,
  dispatch,
  onConfirmClose,
}: {
  form: PostFormState;
  dispatch: Dispatch<any>;
  onConfirmClose?: () => void;
}) {
  if (isFormDirty(form)) {
    dispatch(
      showConfirmModal({
        title: "Discard post?",
        message:
          "Are you sure you want to discard this post? Any unsaved work will be lost.",
        confirmText: "Keep editing",
        cancelText: "Discard post",
        confirmButtonProps: {
          variant: "blue-secondary",
        },
        handleConfirm() {
          closeConfirm(dispatch);
        },
        handleCancel() {
          closeConfirm(dispatch);
          closePostForm(dispatch, form);
          onConfirmClose?.();
        },
      })
    );
  } else {
    closePostForm(dispatch, form);
  }
}

export const closeAllWithConfirm = ({
  forms,
  dispatch,
  onConfirmCloseAll,
}: {
  forms: PostFormState[];
  dispatch: Dispatch<unknown>;
  onConfirmCloseAll: () => void;
}) => {
  if (forms.some((f) => isFormDirty(f))) {
    dispatch(
      showConfirmModal({
        title: "Discard all posts?",
        message: (
          <span>
            Are you sure you want to discard <b>all posts</b>? Any unsaved work
            will be lost.
          </span>
        ),
        confirmText: "Keep editing",
        cancelText: "Discard all posts",
        confirmButtonProps: {
          variant: "blue-secondary",
        },
        handleConfirm() {
          closeConfirm(dispatch);
        },
        handleCancel() {
          closeConfirm(dispatch);
          dispatch(closeBulkCreatePostModal());
          onConfirmCloseAll?.();
        },
      })
    );
  } else {
    dispatch(closeBulkCreatePostModal());
  }
};

export function isFormDirty(form: PostFormState) {
  return form.isDirty && !form.formSaveData && !form.post?.past;
}

function closeConfirm(dispatch: Dispatch<unknown>) {
  dispatch(hideModal(false, "CONFIRM_MODAL"));
}

function closePostForm(dispatch: Dispatch<unknown>, form: PostFormState) {
  dispatch(
    closePostFormInBulkCreate({
      postId: form.post._id,
      actionOnCloseSelected: "select-next",
    })
  );
}
